// frontend/src/components/myClients/routineBoard/RoutineBoard.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Button } from '../../ui/button';
import { Plus, FileDown, Save, Calendar, X, BarChart3 } from 'lucide-react';
import RoutineBoardColumn from './RoutineBoardColumn';
import VolumeRail from './VolumeRail';
import EditorAutosaveBar from '../../ui/EditorAutosaveBar';

export default function RoutineBoard({
  days = [], totalStats, totalCount, getDayStats, exerciseDbReady,
  language, exerciseMuscleMap, exerciseImages, isCoachOrAdmin,
  showNameEditor, routineName, routineDescription, onChangeName, onChangeDescription,
  onAddDay, onImportDay, onSaveAsTemplate, onWeekPlanner, onClose,
  onRenameDay, onDuplicateDay, onRequestDeleteDay, onAddExercise, onOpenMuscleDetail, cardCallbacks,
  onReorderDay, onReorderExercise, onMoveExerciseAcrossDays, autosave,
}) {
  const { t } = useTranslation();
  const [railOpen, setRailOpen] = useState(false); // phone drawer

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Resolve a dnd id to its location.
  const findCard = (id) => {
    for (let d = 0; d < days.length; d++) {
      const idx = (days[d].exercises || []).findIndex(e => e.id === id);
      if (idx !== -1) return { dayIdx: d, exIdx: idx };
    }
    return null;
  };
  const colIdxById = (id) => days.findIndex(d => `col-${d.id}` === id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeType = active.data.current?.type;

    if (activeType === 'column') {
      if (over.data.current?.type !== 'column') return; // dropped on a card/body, not a column
      const from = colIdxById(active.id);
      const to = colIdxById(over.id);
      if (from !== -1 && to !== -1) onReorderDay(from, to);
      return;
    }

    if (activeType === 'card') {
      const src = findCard(active.id);
      if (!src) return;
      // Target day: either dropped on a card, or on a column body (empty area).
      let toDayIdx, toExIdx;
      const overType = over.data.current?.type;
      if (overType === 'column-body') {
        toDayIdx = over.data.current.dayIdx;
        toExIdx = (days[toDayIdx].exercises || []).length;
      } else {
        const dst = findCard(over.id);
        if (!dst) return;
        toDayIdx = dst.dayIdx;
        toExIdx = dst.exIdx;
      }
      if (toDayIdx === src.dayIdx) {
        onReorderExercise(src.dayIdx, src.exIdx, toExIdx);
      } else {
        onMoveExerciseAcrossDays(src.dayIdx, src.exIdx, toDayIdx, toExIdx);
      }
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="routine-board">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border/30 flex-shrink-0">
        <div className="flex-1 min-w-0">
          {showNameEditor ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={routineName || ''} onChange={(e) => onChangeName(e.target.value)}
                className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-border/50 bg-background text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/40"
                placeholder={t('routineEditor.routineNamePlaceholder')} data-testid="routine-name-input" />
              <textarea value={routineDescription || ''} onChange={(e) => onChangeDescription(e.target.value)} rows={1}
                className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                placeholder={t('routineEditor.descriptionPlaceholder')} data-testid="routine-desc-input" />
            </div>
          ) : (
            <>
              <h3 className="text-base sm:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{routineName?.toUpperCase()}</h3>
              <p className="text-xs text-muted-foreground">{days.length} {t('routineEditor.trainingDays')}</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onWeekPlanner} className="border-primary/30 text-primary" data-testid="week-planner-btn">
            <Calendar className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">{t('routineEditor.weekPlanner')}</span>
          </Button>
          {onSaveAsTemplate && <Button variant="outline" size="sm" onClick={onSaveAsTemplate} className="hidden sm:flex" data-testid="save-as-template-btn"><Save className="mr-1.5 h-3.5 w-3.5" />{t('routineEditor.saveAsTemplate')}</Button>}
          {/* Phone-only volume toggle */}
          <Button variant="outline" size="sm" onClick={() => setRailOpen(true)} className="lg:hidden" data-testid="open-volume-rail-btn" aria-label={t('routineEditor.totalWorkSets', { count: totalCount })}>
            <BarChart3 className="h-3.5 w-3.5" />
          </Button>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50" data-testid="close-routine-editor-btn">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Body: board + rail */}
      <div className="flex-1 min-h-0 flex">
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
          <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto lg:overflow-x-auto lg:overflow-y-hidden p-3">
            <div className="flex flex-col lg:flex-row gap-3 h-auto lg:h-full items-stretch">
              <SortableContext items={days.map(d => `col-${d.id}`)} strategy={horizontalListSortingStrategy}>
                {days.map((day, dayIdx) => (
                  <RoutineBoardColumn
                    key={day.id} day={day} dayIdx={dayIdx}
                    dayStats={exerciseDbReady ? getDayStats(dayIdx) : {}}
                    dayCount={days.length} language={language}
                    exerciseMuscleMap={exerciseMuscleMap} exerciseImages={exerciseImages}
                    isCoachOrAdmin={isCoachOrAdmin}
                    onRenameDay={onRenameDay} onDuplicateDay={onDuplicateDay} onDeleteDay={onRequestDeleteDay}
                    onAddExercise={onAddExercise} onOpenMuscleDetail={onOpenMuscleDetail} cardCallbacks={cardCallbacks} />
                ))}
              </SortableContext>
              {/* Add-day ghost column */}
              <div className="w-full lg:w-[260px] lg:flex-shrink-0 flex flex-col gap-2">
                <Button variant="outline" className="border-dashed" onClick={onAddDay} data-testid="add-day-btn">
                  <Plus className="mr-2 h-4 w-4" /> {t('routineEditor.addTrainingDay')}
                </Button>
                {onImportDay && (
                  <Button variant="outline" className="border-dashed" onClick={onImportDay} data-testid="import-day-btn">
                    <FileDown className="mr-2 h-4 w-4" /> {t('routineEditor.importDay')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DndContext>

        {/* Volume rail — always open on lg+ */}
        {exerciseDbReady && (
          <div className="hidden lg:block w-[300px] flex-shrink-0 border-l border-border/30 overflow-y-auto">
            <VolumeRail totalStats={totalStats} totalCount={totalCount} onOpenMuscleDetail={onOpenMuscleDetail} />
          </div>
        )}
      </div>

      {/* Phone drawer for volume */}
      {railOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex" data-testid="volume-rail-drawer">
          <div className="flex-1 bg-black/50" onClick={() => setRailOpen(false)} />
          <div className="w-[280px] max-w-[85vw] bg-background border-l border-border/30 overflow-y-auto">
            <div className="flex justify-end p-2"><button onClick={() => setRailOpen(false)} className="p-2 text-muted-foreground" data-testid="close-volume-rail-btn"><X className="h-5 w-5" /></button></div>
            <VolumeRail totalStats={totalStats} totalCount={totalCount} onOpenMuscleDetail={(s) => { onOpenMuscleDetail?.(s); setRailOpen(false); }} />
          </div>
        </div>
      )}

      {/* Autosave bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-border/30 flex-shrink-0 bg-background/95">
        <EditorAutosaveBar canUndo={autosave.canUndo} canRedo={autosave.canRedo} onUndo={autosave.onUndo} onRedo={autosave.onRedo} status={autosave.status} className="flex-1 justify-end" />
      </div>
    </div>
  );
}
